import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { API } from "aws-amplify";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsSearching,
  setListing,
  setSearchText,
  setSelected,
} from "../store/ducks/cardCode";
import { HeadCell } from "../models/dataTable";
import {
  CreateCardCodeInput,
  UpdateCardCodeInput,
  CardCode,
} from "../models/GQL_API";
import useApp from "./useApp";
import {
  getCardCode,
  listCardCodes,
  cardByCode,
  cardCodesByUserIDAndStartDate,
} from "../graphql/queries";
import {
  createCardCode,
  deleteCardCode,
  updateCardCode,
} from "../graphql/mutations";
import { ListingVariables, TransactionListingVariables } from "../models/app";

const useCardCode = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { session, showConfirm, showError } = useApp();
  // const { tiersGetName } = useTier("tiers", "tier");
  // const { accountsSelected } = useAccount("accounts", "account");
  // const { timelinesCreate } = useTimeline("timelines", "timelines");

  const listing = useSelector(
    (state: any) => state[`${listingName}`]["listing"],
  );
  const searchText = useSelector(
    (state: any) => state[`${listingName}`]["searchText"],
  );
  const selected = useSelector(
    (state: any) => state[`${listingName}`]["selected"],
  );
  const isSearching = useSelector(
    (state: any) => state[`${listingName}`]["isSearching"],
  );

  async function fetchAll() {
    const filter: any = {};

    try {
      let nextToken = null;
      let listing: CardCode[] = [];
      let allListing: CardCode[] = [];

      do {
        const response: any = await API.graphql({
          query: listCardCodes,
          variables: {
            limit: 10000,
            filter,
            nextToken: nextToken,
          },
          authMode: session
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });

        nextToken = response.data.listCardCodes.nextToken;
        listing = response.data.listCardCodes.items;

        allListing = [...allListing, ...listing];
      } while (nextToken !== null && nextToken !== undefined);

      return allListing;
    } catch (err) {
      showError(err);

      throw err;
    }
  }

  async function fetch(props: ListingVariables) {
    let { searchText, limit } = props;

    const filter: any = {};
    filter.or = [];
    filter.and = [];

    if (searchText && searchText.length > 0) {
      searchText = searchText.trim().toLowerCase();
      filter.or = [{ code: { contains: searchText } }];
    }

    if (filter.and && filter.and.length === 0) {
      delete filter.and;
    }
    if (filter.or && filter.or.length === 0) {
      delete filter.or;
    }

    try {
      const response: any = await API.graphql({
        query: listCardCodes,
        variables: {
          limit: limit,
          filter,
          nextToken: null,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      let nextToken = response.data.listCardCodes.nextToken;
      let listing = response.data.listCardCodes.items;

      dispatch(setListing(listing));
      return listing;
    } catch (err) {
      showError(err);
      return [];
    }
  }

  async function fetchByCode(cardCode: string) {
    try {
      const response: any = await API.graphql({
        query: cardByCode,
        variables: {
          code: cardCode,
          filter: { deleted: { eq: "0" } },
          limit: 1,
          nextToken: null,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      let users = response.data.cardByCode.items;

      if (users.length > 0) {
        return users[0];
      }

      return null;
    } catch (err) {
      showError(err);
      return null;
    }
  }

  async function fetchByUser(userID: string) {
    try {
      let filter: any = {
        deleted: { eq: "0" },
      };
      filter.userID = { eq: userID };
      const response: any = await API.graphql({
        query: cardCodesByUserIDAndStartDate,
        variables: {
          userID,
          filter: { deleted: { eq: "0" } },
          limit: 1000,
          nextToken: null,
        },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      let cardCodes = response.data.cardCodesByUserIDAndStartDate.items;

      if (cardCodes.length > 0) {
        dispatch(setListing(cardCodes));
        return cardCodes;
      }

      return [];
    } catch (err) {
      showError(err);
      return null;
    }
  }

  /**
   * Get Resource Name
   *
   * @param id id: string
   *
   * @returns string
   */
  const getName = (id: string) => {
    if (listing.length > 0) {
      const model = listing.find((model: CardCode) => model.id === id);
      return model ? model.name : "";
    } else {
      return "";
    }
  };

  async function get(resourceId: string) {
    try {
      const cardCode: any = await API.graphql({
        query: getCardCode,
        variables: { id: resourceId },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      return cardCode.data.getCardCode;
    } catch (err) {
      showError(err);
      return null;
    }
  }

  async function create(data: any, userID?: string) {
    try {
      // Mandatory Field Validation
      if (!data.code) {
        throw new Error("Please provide card code.");
      }

      if (!userID) {
        throw new Error("Please provide user ID.");
      }

      // Set default values
      const today = new Date();
      const defaultStartDate = today.toISOString().split("T")[0]; // 'YYYY-MM-DD'
      const defaultEndDate = new Date(today.setDate(today.getDate() + 30))
        .toISOString()
        .split("T")[0];

      const createInput: CreateCardCodeInput = {
        userID: userID?.length ? userID : "",
        cardID: data.cardID,
        tier: data.tier.toLowerCase(),
        tier_name: data.tier.toLowerCase(),
        points_balance: data.points_balance,
        code: data.code?.trim() || ``,
        startDate: data.startDate || defaultStartDate,
        endDate: data.endDate || defaultEndDate,
        status: data.status.toLowerCase(),
        createdAt: new Date().toISOString(),
        createdByName: session.name,
      };

      let createdCardCode: any = await API.graphql<CardCode>({
        query: createCardCode,
        variables: { input: createInput },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      createdCardCode = createdCardCode.data.createCardCode;

      dispatch(setListing([createdCardCode, ...listing]));

      showConfirm(`New ${singleName} has been created successfully`);
      fetch({ searchText: "", limit: 1000, startIndex: 0 });
      return createdCardCode;
    } catch (err: Error | any) {
      showError(err.message || err);
      return null;
    }
  }

  async function update(resourceId: string, data: any) {
    try {
      // Mandatory Field Validation
      if (!data.code) {
        throw new Error("Please provide card code.");
      }

      // Get old card code
      const original = await get(resourceId);
      if (!original) {
        showError("Can not find this card Code.");
        return;
      }

      const updateInput: UpdateCardCodeInput = {
        id: original.id,
        code: data.code ? data.code : original!.code,
        startDate: data.startDate ? data.startDate : original!.startDate,
        endDate: data.endDate ? data.endDate : original!.endDate,
        status: data.status ? data.status : original!.status,
        createdAt: data.createdAt ? data.createdAt : original!.createdAt,
        userID: original.userId,
        cardID: data.cardID ? data.cardID : original!.cardID,
        tier: data.tier ? data.tier : original!.tier,
        points_balance: data.points_balance
          ? data.points_balance
          : original!.points_balance,
        createdByName: original!.createdByName,
        _version: original!._version,
      };

      let updatedCardCode: any = await API.graphql<CardCode>({
        query: updateCardCode,
        variables: { input: updateInput },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      updatedCardCode = updatedCardCode.data.updateCardCode;
      showConfirm(`${singleName} has been updated successfully`);
      return updatedCardCode;
    } catch (err: Error | any) {
      showError(err.message || err);
      return null;
    }
  }

  async function trashByUserID(userID: string) {
    try {
      const cardCodes = await fetchByUser(userID);

      for (let cardCode of cardCodes!) {
        await trash(cardCode.id);
      }
    } catch (err) {
      showError(err);
    }
  }

  async function trash(resourceId: string) {
    try {
      const original = await get(resourceId);

      const updated = await API.graphql({
        query: updateCardCode,
        variables: {
          input: {
            id: original.id,
            status: "suspended",
            deleted: "1",
            _version: original!._version,
          },
        },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      dispatch(
        setListing(
          listing.filter((resource: any) => resource.id !== resourceId),
        ),
      );
      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function bulkTrash(resourceIds: any) {
    for (let resourceId of resourceIds) {
      try {
        await trash(resourceId);
      } catch (err: Error | any) {
        showError(err);
      }
    }

    dispatch(
      setListing(
        listing.filter((resource: any) => !resourceIds.has(resource.id)),
      ),
    );

    showConfirm(
      `${resourceIds.size} ${listingName} items has been moved to trash`,
    );
  }

  async function remove(resourceId: any) {
    try {
      console.log("deleted");
      const removedCardCode = await API.graphql({
        query: deleteCardCode,
        variables: {
          input: {
            id: resourceId,
          },
        },
        authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      console.log({
        removedCardCode,
      });

      dispatch(
        setListing(
          listing.filter((resource: any) => resource.id !== resourceId),
        ),
      );

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      console.log(err);
      showError(err);
    }
  }

  async function exportAll(params: TransactionListingVariables) {
    try {
      let exportedData = [];

      const data = await fetch(params);

      for (let cardCode of data!) {
        let row: any = { ...cardCode };
        exportedData.push(row);
      }

      return exportedData;
    } catch (err) {
      showError(err);
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "code",
      numeric: false,
      disablePadding: false,
      label: "Code",
    },
    {
      id: "cardID",
      numeric: false,
      disablePadding: false,
      label: "Card ID",
    },
    {
      id: "points_balance",
      numeric: false,
      disablePadding: false,
      label: "Points",
    },
    {
      id: "startDate",
      numeric: false,
      disablePadding: false,
      label: "Start Date",
    },
    {
      id: "endDate",
      numeric: false,
      disablePadding: false,
      label: "End Date",
    },
    {
      id: "status",
      numeric: false,
      disablePadding: false,
      label: "Status",
    },
    {
      id: "createdByName",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = [
    "code",
    "cardID",
    "points_balance",
    "startDate",
    "endDate",
    "status",
  ];

  const options: any[] = [];
  const api: any = {};

  api[`${listingName}Selected`] = selected;
  api[`${listingName}IsSearching`] = isSearching;

  api[`${listingName}Listing`] = listing;
  api[`${listingName}Options`] = options;
  api[`${listingName}SearchText`] = searchText;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;

  api[`${listingName}FetchAll`] = fetchAll;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchByCode`] = fetchByCode;
  api[`${listingName}FetchByUser`] = fetchByUser;

  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}TrashByUserID`] = trashByUserID;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}GetName`] = getName;
  api[`${listingName}Export`] = exportAll;

  api[`${listingName}ChangeListing`] = (listing: any[]) =>
    dispatch(setListing(listing));
  api[`${listingName}Search`] = (searchText: string) =>
    dispatch(setSearchText(searchText));
  api[`${listingName}ChangeIsSearching`] = (payload: boolean) =>
    dispatch(setIsSearching(payload));
  api[`${listingName}Select`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));

  return api;
};

export default useCardCode;
